// Define the return type for withResolvers
interface PromiseWithResolvers<T> {
    promise: Promise<T>;
    resolve: (value: T | PromiseLike<T>) => void;
    reject: (reason?: any) => void;
}

// Extend the Promise constructor interface
declare global {
    interface PromiseConstructor {
        withResolvers<T>(): PromiseWithResolvers<T>;
    }
}

// Add the implementation if it doesn't exist
if (!Promise.withResolvers) {
    Promise.withResolvers = function <T>(): PromiseWithResolvers<T> {
        let resolve!: (value: T | PromiseLike<T>) => void;
        let reject!: (reason?: any) => void;

        const promise = new Promise<T>((res, rej) => {
            resolve = res;
            reject = rej;
        });

        return { promise, resolve, reject };
    };
}

/**
 * React crashes the page when insertBefore/removeChild runs against a node that
 * is no longer a direct child. Translation, extensions, and our own portals can
 * all move that node. Place the update on a parent that still contains it.
 */
if (typeof Node === "function" && Node.prototype) {
    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function <T extends Node>(this: Node, node: T, child: Node | null): T {
        if (child && child.parentNode !== this) {
            let reference: Node | null = child;
            while (reference && reference.parentNode !== this) {
                reference = reference.parentNode;
            }
            if (reference && reference.parentNode === this) {
                return originalInsertBefore.call(this, node, reference) as T;
            }
            return originalInsertBefore.call(this, node, null) as T;
        }
        return originalInsertBefore.call(this, node, child) as T;
    };

    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
        if (child.parentNode !== this) {
            if (child.parentNode) {
                return originalRemoveChild.call(child.parentNode, child) as T;
            }
            return child;
        }
        return originalRemoveChild.call(this, child) as T;
    };
}

export { };